import { Config } from '@backstage/config';
import { InputError } from '@backstage/errors';
import {
  isValidSearchFilterType,
  SearchFilterConfigType,
  TextFilterMatch,
} from '../filters';
import { CollatorType } from './CollatorType';
import { MARKETPLACE_PROJECTS_DEFAULT_FILTERS } from './MarketplaceProject.defaults';
import { PROVISIONING_CONSUMABLE_DEFAULT_FILTERS } from './ProvisioningConsumable.defaults';
import {
  MarketplaceProjectConfigurationType,
  ProvisioningConsumableConfigurationType,
} from '../types';

export const SEARCH_CONFIG_ROOT = 'mesh.search';
export const SEARCH_MARKETPLACE_UI_CONFIG = 'mesh.marketplace.ui.searchPage';
export const SEARCH_FILTERS_CONFIG_KEY = 'filters';
export const INDEX_FIELDS_CONFIG_KEY = 'indexFields';

const filtersDefaultConfig: CollatorFiltersConfig = {
  [CollatorType.MARKETPLACE_PROJECTS]: MARKETPLACE_PROJECTS_DEFAULT_FILTERS,
  [CollatorType.PROVISIONING_CONSUMABLES]:
    PROVISIONING_CONSUMABLE_DEFAULT_FILTERS,
};
export const getFiltersDefaultConfig = () => filtersDefaultConfig;

export const collatorDefaultConfig = {
  schedule: {
    frequency: { minutes: 10 },
    timeout: { minutes: 15 },
    initialDelay: { seconds: 3 },
  },
  collatorOptions: {
    batchSize: 100,
  },
};

export type CollatorFactoryConfigOptions = {
  batchSize: number;
};

export type VisualDiscoveryConfigOptions = {
  groups: { label: string; path: string }[];
};

export type BusinessConceptsConfigOptions = {
  businessConceptsDescriptorPaths: string[];
};

export type IndexBusinessConcept = {
  descriptorPaths: string[];
};

export type IndexFactor = {
  path: string;
  relevance: string;
};

export type CollatorsIndexFields = {
  [collatorType in CollatorType]: IndexFactor[];
};

export type SearchFilterConfigBase = {
  field: string;
  label: string;
  landscapes?: string[];
  defaultValues?: string[];
  // Optional display label to override label in the UI
  displayLabel?: string;
};

export type SearchGenericFilterConfig = SearchFilterConfigBase & {
  type:
    | SearchFilterConfigType.CHOICE
    | SearchFilterConfigType.BOOLEAN
    | SearchFilterConfigType.DATE
    | SearchFilterConfigType.CONSUMABLE
    | SearchFilterConfigType.DOMAIN
    | SearchFilterConfigType.TYPE
    | SearchFilterConfigType.FAVORITES;
};

export type SearchTextFilterConfig = SearchFilterConfigBase & {
  type: SearchFilterConfigType.TEXT;
  match?: TextFilterMatch;
};

export type OrderByConfig = {
  field: string;
  ascLabel?: string;
  descLabel?: string;
};

export type SearchFilterConfig =
  | SearchGenericFilterConfig
  | SearchTextFilterConfig;

export type CollatorFiltersConfig = {
  [collatorType in CollatorType]: SearchFilterConfig[];
};

export function readIndexFieldsConfigOptions(
  configRoot: Config,
): CollatorsIndexFields {
  const indexFieldsConfig: CollatorsIndexFields = {
    [CollatorType.MARKETPLACE_PROJECTS]: [],
    [CollatorType.PROVISIONING_CONSUMABLES]: [],
  };

  const searchConfig = configRoot.getOptionalConfig(SEARCH_CONFIG_ROOT);
  if (!searchConfig) {
    return indexFieldsConfig;
  }

  searchConfig.keys().forEach(collatorType => {
    if (!Object.values(CollatorType).includes(collatorType as CollatorType)) {
      throw new InputError(
        `Invalid collator type in search configuration: ${collatorType}.
        Valid collator types are: ${Object.values(CollatorType).join(', ')}`,
      );
    }

    const indexFields =
      searchConfig.getOptionalConfigArray(
        `${collatorType}.${INDEX_FIELDS_CONFIG_KEY}`,
      ) ?? [];

    indexFieldsConfig[collatorType as CollatorType] = indexFields
      .map(configField => {
        const path = configField.getOptionalString('path');
        const relevance = configField.getOptionalString('relevance');
        if (path && relevance && ['A', 'B', 'C', 'D'].includes(relevance)) {
          return { path, relevance };
        }
        return undefined;
      })
      .filter(Boolean) as IndexFactor[];
  });

  return indexFieldsConfig;
}

/**
 * Sanitizes and merges custom filters with default filters.
 * - Applies defaultValues from custom filters to matching default filters
 * - Filters out custom filters that conflict with default filter fields/labels
 * - Filters out duplicate fields/labels within custom filters
 *
 * @param defaultFilters - The default filters for the collator type
 * @param customFilters - The custom filters from configuration
 * @param collatorType - The collator type for logging purposes
 * @param logger - Optional logger to warn about skipped filters
 * @returns The merged and sanitized filter array
 */
export function sanitizeAndMergeFilters(
  defaultFilters: SearchFilterConfig[],
  customFilters: SearchFilterConfig[],
  collatorType: CollatorType,
): SearchFilterConfig[] {
  const defaultFields = new Set(defaultFilters.map(f => f.field));
  const defaultLabels = new Set(defaultFilters.map(f => f.label));

  // Validate and normalize custom filters
  const normalizedCustomFilters = customFilters.map(filter => {
    // Decoration-only entries (only field + defaultValues targeting a default filter)
    // do not need type validation — mirrors the old decorateWithDefaults behavior.
    if (defaultFields.has(filter.field)) {
      return filter;
    }
    const type = filter.type.toLowerCase() as SearchFilterConfigType;
    if (!isValidSearchFilterType(type)) {
      throw new InputError(
        `Invalid search filter type in ${collatorType} configuration: '${filter.type}'. ` +
          `Valid search filter types are: ${Object.values(
            SearchFilterConfigType,
          ).join(', ')}`,
      );
    }

    if (type === SearchFilterConfigType.TEXT) {
      const textFilter = filter as SearchTextFilterConfig;
      if (textFilter.match !== undefined) {
        const match = textFilter.match.toLowerCase() as TextFilterMatch;
        if (!Object.values(TextFilterMatch).includes(match)) {
          throw new InputError(
            `Invalid text filter match in ${collatorType} configuration for field '${filter.field}': '${textFilter.match}'. ` +
              `Valid text filter matches are: ${Object.values(
                TextFilterMatch,
              ).join(', ')}`,
          );
        }
        return { ...filter, type, match };
      }
      return { ...filter, type };
    }

    return { ...filter, type };
  });

  const defaultValuesMap: Record<string, string[] | undefined> = {};
  normalizedCustomFilters.forEach(filter => {
    if (filter.defaultValues && defaultFields.has(filter.field)) {
      defaultValuesMap[filter.field] = filter.defaultValues;
    }
  });

  // Decorate default filters with defaultValues from custom config
  const decoratedDefaults = defaultFilters.map(filter => {
    if (defaultValuesMap[filter.field]) {
      return { ...filter, defaultValues: defaultValuesMap[filter.field] };
    }
    return filter;
  });

  // Track seen fields for duplicate detection
  const seenFields = new Set<string>();
  const seenLabels = new Set<string>();

  // Filter and validate custom filters
  const validCustomFilters = normalizedCustomFilters.filter(filter => {
    // Skip if this is just a defaultValues decoration for a default filter
    if (defaultFields.has(filter.field) && filter.defaultValues !== undefined) {
      return false;
    }

    // Check for conflicts with default filters
    if (defaultFields.has(filter.field)) {
      throw new InputError(
        `Found a conflict between a default filter and a custom filter in ${collatorType} search filters configuration: field '${filter.field}'. ` +
          `You cannot define the same field as a default filter and a custom filter.`,
      );
    }
    if (defaultLabels.has(filter.label)) {
      throw new InputError(
        `Found a conflict between a default filter and a custom filter in ${collatorType} search filters configuration: label '${filter.label}'. ` +
          `You cannot define the same label as a default filter and a custom filter.`,
      );
    }

    // Check for duplicates within custom filters
    if (seenFields.has(filter.field)) {
      throw new InputError(
        `Duplicate field in ${collatorType} search filters configuration: '${filter.field}'. ` +
          `You cannot define the same field multiple times.`,
      );
    }
    if (seenLabels.has(filter.label)) {
      throw new InputError(
        `Duplicate label in ${collatorType} search filters configuration: '${filter.label}'. ` +
          `You cannot define the same label multiple times.`,
      );
    }

    seenFields.add(filter.field);
    seenLabels.add(filter.label);
    return true;
  });

  return [...decoratedDefaults, ...validCustomFilters];
}

export function mapCustomFiltersFromConfig(
  filtersByConfig:
    | MarketplaceProjectConfigurationType
    | ProvisioningConsumableConfigurationType
    | undefined,
  taxonomyRef?: string,
): SearchFilterConfig[] {
  return (filtersByConfig ?? [])
    .filter(f => {
      if ('landscapes' in f) {
        const landscapes = f.landscapes as string[];

        if (!taxonomyRef) return true;
        if (!landscapes) return true;
        if (taxonomyRef === 'all') return false;

        return landscapes.includes(taxonomyRef);
      }
      return true;
    })
    .map(f => ({
      field: f.field,
      label: f.label ?? f.field,
      type: f.type ?? SearchFilterConfigType.TEXT,
      defaultValues: f.defaultValues,
      match: f.match,
    }));
}

export function generateMarketplaceProjectsFilters(
  filtersByConfig: MarketplaceProjectConfigurationType | undefined,
  includeDefaultFilters: boolean = true,
  taxonomyRef?: string,
): SearchFilterConfig[] {
  const defaultFilters = includeDefaultFilters
    ? MARKETPLACE_PROJECTS_DEFAULT_FILTERS
    : [];
  const customFilters = mapCustomFiltersFromConfig(
    filtersByConfig,
    taxonomyRef,
  );
  return sanitizeAndMergeFilters(
    defaultFilters,
    customFilters,
    CollatorType.MARKETPLACE_PROJECTS,
  );
}

export function generateProvisioningConsumablesFilters(
  filtersByConfig: ProvisioningConsumableConfigurationType | undefined,
  includeDefaultFilters: boolean = true,
): SearchFilterConfig[] {
  const defaultFilters = includeDefaultFilters
    ? PROVISIONING_CONSUMABLE_DEFAULT_FILTERS
    : [];
  const customFilters = mapCustomFiltersFromConfig(filtersByConfig);
  return sanitizeAndMergeFilters(
    defaultFilters,
    customFilters,
    CollatorType.PROVISIONING_CONSUMABLES,
  );
}
